// Socket access for Svelte components. The WorldSocket itself lives in net/socket.ts;
// this module holds the singleton, reactive connection/presence state, and a
// `listen()` helper that subscribes for the lifetime of the calling component.

import { WorldSocket } from "../../net/socket.ts";
import type { ClientMessage, OnlinePlayer, ServerMessage } from "@ygoevo/world-protocol";
import { getToken, clearToken } from "../auth.ts";

let socket: WorldSocket | null = null;

export const net = $state({ connected: false });
/** App-wide global presence. This deliberately outlives every route/screen so the
 *  roster cannot miss an update while the player is dueling, watching a replay,
 *  editing a deck, or between world mounts. */
export const presence = $state({ players: [] as OnlinePlayer[], synced: false });

/** Called once at boot (main.ts), before the socket is connected. */
export function initNet(s: WorldSocket): void {
  socket = s;
  s.on("Online", (m) => {
    presence.players = m.players;
    presence.synced = true;
  });
  s.onOpen(() => {
    net.connected = true;
    presence.synced = false;
    // Authenticate the (re)connected socket with our stored login token. The server
    // replies LoginOk, which App routes on. No token → the socket stays anonymous
    // until the player signs in via the Discord broker.
    const token = getToken();
    if (token) s.send({ t: "Auth", token });
  });
  s.onClose(() => {
    net.connected = false;
    presence.players = [];
    presence.synced = false;
  });
  // App-level keepalive. The Cloudflare world DO has no protocol-level ping (workerd's WebSocket
  // can't send one), so silence is all the server has to tell a dead client from an idle one: it
  // reaps sockets quiet for 90s (apps/world-cf/src/worldDO.ts DEAD_MS). A player standing still
  // sends nothing for minutes, so ping on a timer. Background tabs throttle timers to ~1/min, which
  // still clears the reap window. Skipped while disconnected so an outage can't build a ping backlog
  // in the socket's replay queue.
  setInterval(() => {
    if (net.connected) s.send({ t: "Ping", nonce: 0 });
  }, 25_000);
}

export function getSocket(): WorldSocket {
  if (!socket) throw new Error("net not initialised");
  return socket;
}

/** Sign out: revoke the token server-side over the socket, forget it locally, and
 *  drop to a fresh anonymous connection. */
export function logout(): void {
  const s = getSocket();
  try {
    s.send({ t: "Logout" });
  } catch {
    /* ignore, clearing the token + reconnect is enough client-side */
  }
  clearToken();
  s.reconnect();
}

export function send(m: ClientMessage): void {
  getSocket().send(m);
}

/**
 * Subscribe to a server message for as long as the calling component is mounted.
 * Must be called during component init (it registers a `$effect` whose teardown
 * unsubscribes). Returns nothing, cleanup is automatic.
 */
export function listen<K extends ServerMessage["t"]>(
  t: K,
  fn: (m: Extract<ServerMessage, { t: K }>) => void,
): void {
  $effect(() => getSocket().on(t, fn));
}
